import React, { useEffect, useState } from "react";
import axios from "axios";
import { Coins, ArrowRight, Sparkles, Share2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CoinsShareBanner — compact vibrant banner shown above the post composer on Feed.
 * Reuses the same value-prop copy as the landing hero but sized to fit inline
 * on a scrollable timeline.
 */
export default function CoinsShareBanner() {
  const [live, setLive] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = () => axios.get(`${API}/hero/live-coins-today`)
      .then((r) => { if (mounted) setLive(r.data); })
      .catch(() => {});
    load();
    const iv = setInterval(load, 45000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  const coins24 = live?.total_coins_24h ?? 0;

  return (
    <section
      data-testid="coins-share-banner"
      className="relative overflow-hidden rounded-2xl shadow-[0_16px_40px_-18px_rgba(234,179,8,0.35)]"
      style={{
        background:
          "linear-gradient(120deg, #f59e0b 0%, #f97316 25%, #dc2626 55%, #7c3aed 85%, #2563eb 100%)",
      }}
    >
      {/* grain overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-15 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 25%, rgba(255,255,255,0.35) 0, transparent 40%), radial-gradient(circle at 85% 65%, rgba(0,0,0,0.25) 0, transparent 45%)",
        }}
      />

      {/* floating coins */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <span className="absolute top-3 left-4 text-white/25 animate-[floatY_5s_ease-in-out_infinite]">
          <Coins className="w-6 h-6" />
        </span>
        <span
          className="absolute bottom-4 right-16 text-white/20 animate-[floatY_6.5s_ease-in-out_infinite]"
          style={{ animationDelay: "0.4s" }}
        >
          <Coins className="w-9 h-9" />
        </span>
        <span
          className="absolute top-6 right-6 text-white/25 animate-[floatY_4.5s_ease-in-out_infinite]"
          style={{ animationDelay: "0.8s" }}
        >
          <Coins className="w-4 h-4" />
        </span>
      </div>

      <style>{`
        @keyframes floatY {
          0%, 100% { transform: translateY(0px) rotate(-2deg); }
          50%      { transform: translateY(-10px) rotate(3deg); }
        }
      `}</style>

      <div className="relative z-10 px-4 sm:px-6 py-4 flex items-center gap-3">
        <div className="hidden sm:flex w-11 h-11 rounded-2xl bg-white/20 backdrop-blur border border-white/25 text-white items-center justify-center shrink-0">
          <Share2 className="w-5 h-5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur border border-white/25 text-white text-[10px] font-black uppercase tracking-widest" data-testid="coins-share-badge">
            <Sparkles className="w-3 h-3" /> Compartilhe & ganhe
          </div>
          <h3
            className="text-white text-base sm:text-lg font-black tracking-tight leading-snug mt-1 drop-shadow"
            data-testid="coins-share-title"
          >
            No <span className="inline-flex items-center gap-0.5"><Coins className="w-4 h-4 text-yellow-200" />Futuro PDV</span> você ganha moedas por tudo que faz.
          </h3>
          <p className="text-white/90 text-xs sm:text-sm mt-0.5 leading-snug drop-shadow" data-testid="coins-share-copy">
            Transfira para seus amigos na hora.{" "}
            <span className="inline-flex items-center gap-0.5 font-black text-white">
              Vem para o futuro! <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </p>
        </div>

        {coins24 > 0 && (
          <div
            data-testid="coins-share-live"
            className="hidden md:flex items-center gap-2 rounded-xl bg-black/30 backdrop-blur border border-white/15 px-3 py-2 shrink-0"
          >
            <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-lg bg-yellow-300 text-yellow-950">
              <Coins className="w-3.5 h-3.5" />
              <span className="absolute inset-0 rounded-lg border-2 border-yellow-200/60 animate-ping" />
            </span>
            <div className="leading-tight text-white">
              <div className="text-[9px] uppercase tracking-widest opacity-75 font-black">Hoje</div>
              <div className="font-black text-sm">
                {coins24.toLocaleString("pt-BR")} <span className="text-[10px] font-bold opacity-85">moedas</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
